import { WebChatRenderEvent } from './web-chat.models';

const TERMINAL_PRESENTATION_STATES = new Set([
  'cancelled',
  'canceled',
  'completed',
  'durable',
  'failed',
  'rejected',
]);

export function hasPresentationEnvelopes(
  renderEvent: WebChatRenderEvent | null | undefined
): boolean {
  return (renderEvent?.presentations?.length ?? 0) > 0;
}

export function isTerminalPresentationState(
  renderEvent: WebChatRenderEvent | null | undefined
): boolean {
  return TERMINAL_PRESENTATION_STATES.has(renderEvent?.state?.trim().toLowerCase() ?? '');
}

export function resolvePresentationRenderEventKey(
  renderEvent: WebChatRenderEvent | null | undefined
): string | null {
  if (!renderEvent) {
    return null;
  }

  const presentationId = renderEvent.presentations
    ?.map((presentation) => normalizeIdentifier(presentation.presentationId))
    .find((candidate): candidate is string => candidate != null);
  if (presentationId) {
    return `presentation:${presentationId.toLowerCase()}`;
  }

  const correlationId = normalizeIdentifier(renderEvent.correlationId);
  return correlationId ? `correlation:${correlationId.toLowerCase()}` : null;
}

export function isPresentationLifecycleEvent(
  renderEvent: WebChatRenderEvent,
  retainedEvents: readonly WebChatRenderEvent[] | null | undefined
): boolean {
  if (hasPresentationEnvelopes(renderEvent)) {
    return true;
  }

  const eventKey = resolvePresentationRenderEventKey(renderEvent);
  const correlationKey = resolvePresentationRenderEventCorrelationKey(renderEvent);
  return (retainedEvents ?? []).some((candidate) =>
    (eventKey != null && resolvePresentationRenderEventKey(candidate) === eventKey) ||
    (correlationKey != null &&
      resolvePresentationRenderEventCorrelationKey(candidate) === correlationKey)
  );
}

export function upsertPresentationRenderEvent(
  currentEvents: readonly WebChatRenderEvent[] | null | undefined,
  renderEvent: WebChatRenderEvent,
  maxHistory: number
): WebChatRenderEvent[] {
  const eventKey = resolvePresentationRenderEventKey(renderEvent);
  const correlationKey = hasPresentationEnvelopes(renderEvent)
    ? null
    : resolvePresentationRenderEventCorrelationKey(renderEvent);
  const retainedEvents = (currentEvents ?? []).filter((candidate) => {
    if (candidate.sequence === renderEvent.sequence) {
      return false;
    }

    if (eventKey != null && resolvePresentationRenderEventKey(candidate) === eventKey) {
      return false;
    }

    return correlationKey == null ||
      resolvePresentationRenderEventCorrelationKey(candidate) !== correlationKey;
  });

  if (!hasPresentationEnvelopes(renderEvent)) {
    return retainedEvents;
  }

  return [renderEvent, ...retainedEvents]
    .sort((left, right) => right.sequence - left.sequence)
    .slice(0, maxHistory);
}

function normalizeIdentifier(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function resolvePresentationRenderEventCorrelationKey(
  renderEvent: WebChatRenderEvent | null | undefined
): string | null {
  const correlationId = normalizeIdentifier(renderEvent?.correlationId);
  return correlationId ? `correlation:${correlationId.toLowerCase()}` : null;
}
